/**
 * New orders for the pharmacist queue, straight from the `order.placed` push (Rx-01).
 *
 * The push carries the queue row (backend EVENTS["order.placed"].push_data), so the order is added
 * to every cached queue list it belongs in without asking the server. The queue is newest first,
 * so it goes on top of the first page.
 */

import type { InfiniteData, QueryClient } from "@tanstack/react-query";

import type { OrderSummary, PaginatedQueue, QueueOrder } from "../api/orders";

export type QueueTab = "mine" | "unassigned" | "all";

/** The queue row a pushed `order.placed` describes, or null if the push doesn't carry one. */
export function orderFromPush(data: Record<string, string | undefined>): OrderSummary | null {
  const { order_id: id, order_ref, status_code, created_at } = data;
  if (!id || !order_ref || !status_code || !created_at || Number.isNaN(Date.parse(created_at))) return null;
  const lineCount = Number(data.line_count);
  return {
    id,
    order_id: order_ref,
    prescription_id: data.prescription_id ?? "",
    for_family_member_id: data.for_family_member_id ?? null,
    status_code,
    status_label: data.status_label ?? status_code,
    final_amount: "0.00", // not priced yet
    line_count: Number.isInteger(lineCount) && lineCount >= 0 ? lineCount : 0,
    created_at,
  };
}

/** Whether a new, unclaimed order shows in a queue tab with this order-number search. */
export function belongsIn(tab: QueueTab, search: string, order: OrderSummary): boolean {
  if (tab === "mine") return false; // nobody has claimed it yet
  const term = search.trim().toLowerCase(); // the server matches case-insensitively, anywhere
  return term === "" || order.order_id.toLowerCase().includes(term);
}

/** The cached pages with the order on top; unchanged when it is already listed or nothing is cached. */
export function withNewOrder(
  data: InfiniteData<PaginatedQueue> | undefined,
  order: OrderSummary,
): InfiniteData<PaginatedQueue> | undefined {
  if (!data || data.pages.length === 0) return data;
  if (data.pages.some((page) => page.data.some((o) => o.id === order.id))) return data;
  const [first, ...rest] = data.pages;
  return { ...data, pages: [{ ...first, data: [order, ...first.data], total: first.total + 1 }, ...rest] };
}

/** Rows across loaded pages, each order once: adding one on top shifts the server's page
 *  boundaries, so the next page can repeat the previous page's last row. */
export function uniqueRows(data: InfiniteData<PaginatedQueue> | undefined): QueueOrder[] {
  const seen = new Set<string>();
  return (data?.pages ?? []).flatMap((page) => page.data).filter((o) => !seen.has(o.id) && seen.add(o.id));
}

/** Add the order to every cached queue list (queryKey ["queue", tab, search]) it belongs in. */
export function addToQueueCache(qc: QueryClient, order: OrderSummary): void {
  for (const [key, data] of qc.getQueriesData<InfiniteData<PaginatedQueue>>({ queryKey: ["queue"] })) {
    const [, tab, search] = key as [string, QueueTab, string];
    if (belongsIn(tab, search ?? "", order)) qc.setQueryData(key, withNewOrder(data, order));
  }
}
